//ResumeList.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import MainLayout from '../../layouts/MainLayout';
import logo from '../../assets/logo.svg';

const ResumeList = () => {
  const authState = useSelector(state => state.authReducer);
  const { isLoggedIn } = authState;
  const location = useLocation();
  const [resumes, setResumes] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedResumeId, setSelectedResumeId] = useState(null);

  useEffect(() => {
    document.title = authState.isLoggedIn ? `${authState.user.name}'s dashboard` : "Think5Whys";
  }, [authState]);

  const fetchResumes = useCallback(() => {
    const config = {
      url: "/resume/myresume",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId: authState.user._id}
    };
    // console.log("My Resume Search Payload: ", config);
  
    fetchData(config, { showSuccessToast: false }).then(data => setResumes(data.resumes));
  }, [authState.token, fetchData, authState.user._id]);
  
  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchResumes();
  }, [authState.isLoggedIn, fetchResumes]);

  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  
  const deleteClicked = () => {
    setIsDeleteClicked(true);
  }
  
  const noDeleteClicked = () => {
    setIsDeleteClicked(false);
  }

  const handleDelete = (id) => {
    const config = {
      url: `/resume/${id}`,
      method: "delete",
      headers: { Authorization: authState.token }
    };
    fetchData(config).then(() => fetchResumes());
  }

  const handleView = (resumeId) => {
    setSelectedResumeId(prevId => (prevId === resumeId ? null : resumeId));
  };

  const handleCloseResumeDetails = () => {
    setSelectedResumeId(null);
  };

  const handleSearch = () => {
    // Implement search functionality
  };

  const handleResumeClick = (resumeId) => {
    setSelectedResumeId(resumeId);
  };

  const handleResumeShare = (resumeId) => {
    setSelectedResumeId(resumeId);
  };

  const handleResumeSave = (resumeId) => {
    setSelectedResumeId(resumeId);
  };

  const handleResumePrint = (resumeId) => {
    setSelectedResumeId(resumeId);
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

  const sortedResumes = [...resumes].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  return (
    <div className="bg-black [&_p]:text-white p-4">
      <h2 className="text-lg mt-1 mx-8 border-b border-b-gray-300 text-white">My Resumes</h2>

      <div className="my-4 mx-auto py-4 max-w-5xl">
        <div className="border border-gray-300 bg-gray-800 rounded-lg p-6 shadow-lg">
          {resumes && resumes.length !== 0 && (
            <div className="flex justify-between items-center border-b border-b-gray-600 pb-4">
              <h3 className="text-yellow-200 text-lg">{resumes.length} Found!</h3>
              <Link to="/resume/add" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700">
                <FontAwesomeIcon icon={faPlus} /> Add New
              </Link>
            </div>
          )}

          {loading ? (
            <Loader />
          ) : resumes && resumes.length === 0 ? (
            <div className="h-[300px] flex flex-col items-center justify-center text-yellow-300">
              <h3 className="text-lg text-orange-400">No Record Found!</h3>
              <Link to="/resume/add" className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700">
                <FontAwesomeIcon icon={faPlus} /> Add New
              </Link>
            </div>
          ) : (
            sortedResumes.map((resume, index) => (
              <div key={resume._id} className="bg-gray-900 text-white my-4 p-4 rounded-lg shadow-md">
                <div className="flex items-center gap-4">
                  <span className="font-medium text-lg">{index + 1}. {resume.title}</span>
                  <div className="ml-auto flex gap-4">
                    <Tooltip text="View details" position="top">
                      <span className="text-blue-500 cursor-pointer" onClick={() => handleView(resume._id)}>
                        <i className="fa-solid fa-eye"></i>
                      </span>
                    </Tooltip>
                    <Tooltip text="Edit this record" position="top">
                      <Link to={`/resume/${resume._id}`} className="text-green-500">
                        <i className="fa-solid fa-pen"></i>
                      </Link>
                    </Tooltip>
                    <Tooltip text="Delete this record" position="top">
                      <span className="text-red-500 cursor-pointer" onClick={deleteClicked}>
                        <i className="fa-solid fa-trash"></i>
                      </span>
                    </Tooltip>
                  </div>
                </div>

                {selectedResumeId === resume._id && (
                  <div className="bg-gray-700 text-white mt-4 p-4 rounded-lg">
                    <p><strong className="text-yellow-400">Resume Code:</strong> {resume.resumeCode}</p>
                    <p><strong>Title:</strong> {resume.title}</p>
                    <p><strong>Resume Details:</strong> {resume.resumeSummary}</p>
                    <p><strong>Remarks:</strong> {resume.remarks}</p>
                    <p><strong>Status:</strong> {resume.isActive ? "Active" : "Inactive"}</p>
                    <p><strong>Date Created:</strong> {resume.dateCreated ? formatDateTime(resume.dateCreated) : 'N/A'}</p>
                    <div className="flex gap-4 mt-4">
                      <Tooltip text="Share" position="top">
                        <span className="text-green-500 cursor-pointer" onClick={() => handleResumeShare(resume._id)}>
                          <i className="fa-solid fa-share"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text="Save" position="top">
                        <span className="text-blue-500 cursor-pointer" onClick={() => handleResumeSave(resume._id)}>
                          <i className="fa-solid fa-save"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text="Print" position="top">
                        <span className="text-orange-500 cursor-pointer" onClick={() => handleResumePrint(resume._id)}>
                          <i className="fa-solid fa-print"></i>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                )}

                {isDeleteClicked && (
                  <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                    <div className="bg-gray-800 text-white p-6 rounded-lg shadow-lg">
                      <p className="text-lg mb-4">Are you sure you want to delete this resume?</p>
                      <div className="flex gap-4">
                        <button className="bg-red-600 px-4 py-2 rounded-lg shadow hover:bg-red-700" onClick={() => handleDelete(resume._id)}>Confirm Delete</button>
                        <button className="bg-yellow-500 px-4 py-2 rounded-lg shadow hover:bg-yellow-600" onClick={noDeleteClicked}>Cancel</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default ResumeList